/**
 * KAN-926: список "Требуют внимания" по разделам с заголовком и числом строк.
 * Одна разметка для дашборда, /m и мобильной доски; контейнер строк (ul/div)
 * задаёт вызывающий через renderItems.
 * counts (section_counts backend) дают число раздела по всему списку; раздел, строк которого
 * на странице нет (лимит), показывается заголовком и кнопкой "Показать" (onShowMore).
 * KAN-959: раздел сворачивается по шеврону в заголовке, память в useCollapsedSections.
 */
import type { ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useCollapsedSections } from '@/hooks/useCollapsedSections';
import { groupAttentionSections, type AttentionSectionCounts } from '@/lib/attentionSections';

interface AttentionSectionTitleProps {
  title: string;
  count: number;
  /** Без onToggle заголовок без шеврона. */
  expanded?: boolean;
  onToggle?: () => void;
}

export function AttentionSectionTitle({
  title,
  count,
  expanded = true,
  onToggle,
}: AttentionSectionTitleProps): JSX.Element {
  const content = (
    <>
      {title}
      <span data-testid="attention-section-count" className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">
        {count}
      </span>
    </>
  );
  const Chevron = expanded ? ChevronDown : ChevronRight;
  return (
    <h3
      data-testid="attention-section-title"
      className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
    >
      {onToggle ? (
        <button
          type="button"
          data-testid="attention-section-toggle"
          aria-expanded={expanded}
          onClick={onToggle}
          className="-ml-1 flex items-center gap-2 rounded px-1 py-0.5 uppercase tracking-wide hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          <Chevron aria-hidden="true" className="h-4 w-4 shrink-0" />
          {content}
        </button>
      ) : (
        content
      )}
    </h3>
  );
}

interface AttentionSectionsProps<T> {
  items: readonly T[];
  sectionOf: (item: T) => string | null | undefined;
  renderItems: (items: T[]) => ReactNode;
  /** Числа разделов по всему списку; без них число по показанным строкам. */
  counts?: AttentionSectionCounts | null;
  /** Догрузить список; кнопка в разделе без строк на странице. */
  onShowMore?: () => void;
  showMoreDisabled?: boolean;
}

export function AttentionSections<T>({
  items,
  sectionOf,
  renderItems,
  counts,
  onShowMore,
  showMoreDisabled = false,
}: AttentionSectionsProps<T>): JSX.Element {
  const { isCollapsed, toggle } = useCollapsedSections();
  return (
    <div className="flex min-w-0 flex-col gap-4">
      {groupAttentionSections(items, sectionOf, counts).map((group) => {
        const collapsed = isCollapsed(group.code);
        return (
          <section
            key={group.code}
            data-testid="attention-section"
            data-section={group.code}
            aria-label={group.title}
            className="flex min-w-0 flex-col gap-2"
          >
            <AttentionSectionTitle
              title={group.title}
              count={group.count}
              expanded={!collapsed}
              onToggle={() => toggle(group.code)}
            />
            {collapsed
              ? null
              : group.items.length > 0
                ? renderItems(group.items)
                : onShowMore && (
                    <button
                      type="button"
                      data-testid="attention-section-show"
                      disabled={showMoreDisabled}
                      onClick={onShowMore}
                      className="self-start text-sm text-blue-600 hover:underline disabled:opacity-50"
                    >
                      Показать
                    </button>
                  )}
          </section>
        );
      })}
    </div>
  );
}
